import { useRef, useState } from 'react';
import { ArrowUp, FileText, Image, Paperclip, Square, X } from 'lucide-react';
import Button from '@/components/primitives/Button.jsx';
import Icon from '@/components/primitives/Icon.jsx';
import Spinner from '@/components/primitives/Spinner.jsx';
import uploadAttachment from '@/redux/requests/uploadAttachment.js';
import toMediaKind from '../lib/toMediaKind.js';

/**
 * Where the person writes a request. Files upload as soon as they are added and show their
 * progress; Enter sends, Shift+Enter adds a line. While a run is in progress the send button adds
 * the text to the running request, and Stop asks Pieve to stop.
 * `onSubmit({ prompt, attachments })` resolves to { success } and the field clears on success.
 */
const Composer = ({ onSubmit, onStop, isRunning = false, threadId = null, placeholder, variant = 'thread' }) => {
  const [prompt, setPrompt] = useState('');
  const [files, setFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const isUploading = files.some((file) => file.status === 'uploading');
  const uploaded = files.filter((file) => file.status === 'ready').map((file) => file.uploadResult);
  const canSend = !isSending && !isUploading && (prompt.trim().length > 0 || (!isRunning && uploaded.length > 0));

  const updateFile = (key, patch) => setFiles((current) => current.map((file) => (file.key === key ? { ...file, ...patch } : file)));

  const addFiles = (fileList) => {
    for (const file of Array.from(fileList || [])) {
      const key = `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;
      setFiles((current) => [...current, { key, name: file.name, mediaKind: toMediaKind(file), status: 'uploading', percent: 0 }]);
      uploadAttachment({
        threadId,
        file,
        mediaKind: toMediaKind(file),
        onProgress: (progress) => {
          const percent = progress?.phase === 'completing' ? progress.percent : Math.round(((progress?.uploadedBytes || 0) / (progress?.totalBytes || 1)) * 70);
          updateFile(key, { percent });
        }
      }).then((res) => {
        updateFile(key, res?.success ? { status: 'ready', uploadResult: res.data, percent: 100 } : { status: 'failed', error: res?.message || 'Upload failed' });
      });
    }
  };

  const submit = async () => {
    if (!canSend) {
      return;
    }
    setIsSending(true);
    const res = await onSubmit({ prompt: prompt.trim(), attachments: isRunning ? [] : uploaded });
    setIsSending(false);
    if (res?.success !== false) {
      setPrompt('');
      if (!isRunning) {
        setFiles([]);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      className={`assistant-composer assistant-composer--${variant}`}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}>
      <label className="assistant-composer__label" htmlFor={`composer-${variant}`}>
        {isRunning ? 'Add to the running request' : 'Request'}
      </label>
      <textarea id={`composer-${variant}`} className="assistant-composer__field" value={prompt} rows={variant === 'home' ? 3 : 1} placeholder={placeholder} onChange={(event) => setPrompt(event.target.value)} onKeyDown={handleKeyDown} />
      {files.length > 0 && (
        <ul className="assistant-composer__files" aria-label="Attached files">
          {files.map((file) => (
            <li key={file.key} className="assistant-composer__file" data-status={file.status}>
              {file.status === 'uploading' ? <Spinner size={14} /> : <Icon icon={file.mediaKind === 'image' ? Image : FileText} size="sm" />}
              <span className="assistant-composer__file-name">{file.name}</span>
              <span className="assistant-composer__file-note">{file.status === 'uploading' ? `${file.percent}%` : file.status === 'failed' ? file.error : ''}</span>
              <button type="button" className="assistant-composer__file-remove" aria-label={`Remove ${file.name}`} onClick={() => setFiles((current) => current.filter((item) => item.key !== file.key))}>
                <Icon icon={X} size="sm" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="assistant-composer__bar">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <Button variant="ghost" size="sm" iconStart={Paperclip} disabled={isRunning} onClick={() => fileInputRef.current?.click()}>
          Attach
        </Button>
        <span className="assistant-composer__hint">Enter to send, Shift+Enter for a new line</span>
        {isRunning && onStop && (
          <Button variant="secondary" size="sm" iconStart={Square} onClick={onStop}>
            Stop
          </Button>
        )}
        <Button type="submit" variant="primary" size="sm" iconStart={ArrowUp} loading={isSending} disabled={!canSend}>
          {isRunning ? 'Add' : 'Send'}
        </Button>
      </div>
    </form>
  );
};

export default Composer;
